import {useLocation} from "react-router-dom";
import FormsControlCentre, { FormContext, formDate } from "@/components/FormsControlCentre";
import { optionId, optionLabel, formRows, formErrors, focusFormError, formErrorMessage, triggeredRules, emptyAnswer } from "@/lib/formLogic";
import FormRequirements from "@/components/FormRequirements";
import FormAssignments from "@/components/FormAssignments";
import { useEffect, useMemo, useState } from "react";
import { useSafetyNavigate as useNavigate } from "@/lib/safetyNavigation";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Trash2, GripVertical, Pencil, Eye, FileText, ClipboardList, Camera,
  Paperclip, MapPin, PenLine, ToggleLeft, ListChecks, Calendar as CalendarIcon, Clock as ClockIcon,
  Hash, AlignLeft, Type, ChevronDown, ChevronUp, Loader2, X,
} from "lucide-react";
import { toast } from "sonner";
import { SignaturePad } from "@/components/SignaturePad";
import { AuthImage, PhotoGrid } from "@/components/AuthImage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { TemplateLibraryDialog } from "@/components/TemplateLibraryDialog";
import { Globe, ShieldCheck, FileDown, Briefcase, Truck, Copy } from "lucide-react";

export const FIELD_TYPES = [
  {value: "quantity", label: "Quantity + unit", icon: Hash},
  {value: "currency", label: "Currency", icon: Hash},
  {value: "datetime", label: "Date and time", icon: CalendarIcon},
  { value: "short_text", label: "Short text", icon: Type },
  { value: "long_text",  label: "Long text",  icon: AlignLeft },
  { value: "number",     label: "Number",     icon: Hash },
  { value: "yes_no",     label: "Yes / No",   icon: ToggleLeft },
  { value: "radio",      label: "Single choice (radio)", icon: ListChecks },
  { value: "checkbox",   label: "Multi choice (checkboxes)", icon: ListChecks },
  { value: "dropdown",   label: "Dropdown",   icon: ChevronDown },
  { value: "date",       label: "Date",       icon: CalendarIcon },
  { value: "time",       label: "Time",       icon: ClockIcon },
  { value: "photo",      label: "Photo upload", icon: Camera },
  { value: "file",       label: "File upload",  icon: Paperclip },
  { value: "signature",  label: "Signature",  icon: PenLine },
  { value: "location",   label: "GPS location", icon: MapPin },
];
export const TYPE_LABEL = Object.fromEntries(FIELD_TYPES.map((t) => [t.value, t.label]));
export const TYPE_ICON  = Object.fromEntries(FIELD_TYPES.map((t) => [t.value, t.icon]));

export const newFieldId = () => "f_" + Math.random().toString(36).slice(2, 9);

// ---------- Upload helpers ----------
async function uploadFile(file, category = "form", onlineOnly = false) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    __flOnlineOnly: onlineOnly,
  });
  return data.storage_path;
}

async function uploadDataUrl(dataUrl, category = "form_signature", onlineOnly = false) {
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], `${category}-${Date.now()}.png`, { type: "image/png" });
  return uploadFile(file, category, onlineOnly);
}

// ============================================================
//  MAIN PAGE
// ============================================================
export default function Forms() {
  const location = useLocation();
  const { user } = useAuth();
  const nav = useNavigate();
  const isOffice = ["admin", "office", "super_admin"].includes(user.role);
  // Forms is a single combined library showing ALL custom forms (general/job/safety/fleet).
  // Safety permits stay filterable via the category selector — we no longer hard-lock the
  // list to safety-only when opened from the Health & Safety workspace.
  const safetyContext = false;
  const requestedSurface = new URLSearchParams(window.location.search).get("surface");
  const requestedJob = new URLSearchParams(window.location.search).get("job_id") || undefined;
  const [assigning, setAssigning] = useState(null);
  const [assignmentOptions, setAssignmentOptions] = useState({ jobs: [], workers: [] });

  const [subOffset, setSubOffset] = useState(0);
  const [filters, setFilters] = useState({ review_state: new URLSearchParams(window.location.search).get("review") ? "required" : "", template_id: "", job_id: requestedJob || "", worker_id: "", from_date: "", to_date: "" });
  const [tab, setTab] = useState(isOffice ? (new URLSearchParams(window.location.search).has("review") ? "submissions" : new URLSearchParams(window.location.search).has("fill") ? "templates" : "overview") : "fill");
  const [libraryFilters, setLibraryFilters] = useState({search: "", category: "", active: ""});
  const [libraryOffset, setLibraryOffset] = useState(0);
  useEffect(() => { if (isOffice && tab !== "overview") api.get("/form-assignments/options").then(({data})=>setAssignmentOptions(data)).catch(()=>{}); }, [isOffice,tab]);
  const navigatePanel = (next, patch={}) => { setTab(next); if(next==='submissions'){setFilters({review_state:'',template_id:'',job_id:requestedJob||'',worker_id:'',from_date:'',to_date:'',...patch});setSubOffset(0);} };
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Builder dialog state (office)
  // Builder is a full page now (/app/forms/new, /app/forms/:id/edit)

  // Filler dialog state (any role)
  const [fillingTemplate, setFillingTemplate] = useState(null);

  // Submission viewer
  const [viewingSubId, setViewingSubId] = useState(new URLSearchParams(window.location.search).get("submission"));
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [fillJobId, setFillJobId] = useState("");

  const loadAll = async () => {
    if(isOffice && tab === "overview"){setLoading(false);return;}
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/form_templates", {params: isOffice && tab === "templates" ? {...Object.fromEntries(Object.entries(libraryFilters).filter(([,v])=>v!=="")),category:safetyContext?"safety":libraryFilters.category||undefined,offset:libraryOffset,limit:30} : {category:safetyContext?"safety":undefined}}),
        isOffice && tab !== "submissions" ? Promise.resolve({data:[]}) : api.get("/form_submissions", { params: { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), category: safetyContext ? "safety" : filters.category || undefined, offset: subOffset, limit: 50 } }),
      ]);
      setTemplates(safetyContext ? t.data.filter(t => t.category === "safety") : t.data);
      setSubmissions(s.data);
    } catch (e) {
      toast.error("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const timer=setTimeout(loadAll,150); const refresh=()=>loadAll(); window.addEventListener("forms-changed",refresh); return ()=>{clearTimeout(timer);window.removeEventListener("forms-changed",refresh);}; }, [subOffset, filters, safetyContext, tab, libraryFilters, libraryOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNewBuilder = () => nav("/app/forms/new");
  const openEditBuilder = (t) => nav(`/app/forms/${t.id}/edit`);

  // Resolve direct links independently of the current library page.
  useEffect(() => {
    let active=true;
    const params=new URLSearchParams(location.search),tid=params.get('fill'),sid=params.get('submission');
    if(sid)setViewingSubId(sid);
    if(params.has('review')&&isOffice){setTab('submissions');setFilters(f=>({...f,review_state:'required',job_id:params.get('job_id')||''}));setSubOffset(0);}
    if(tid)api.get(`/form_templates/${encodeURIComponent(tid)}`).then(({data:t})=>{if(active&&t.active){setFillJobId(params.get('job')||'');setFillingTemplate(t);}}).catch(()=>{if(active)toast.error('This form is unavailable');});
    return ()=>{active=false;};
  }, [location.search,isOffice]);

  const shareTemplate = async (t) => {
    try {
      await api.patch(`/form_templates/${t.id}`, { shared: !t.shared });
      toast.success(t.shared
        ? "Removed from the shared library"
        : "Shared! Other companies can now find and import this template");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const archiveTemplate = async (t) => {
    try {
      await api.patch(`/form_templates/${t.id}`, { active: !t.active });
      toast.success(t.active ? "Form archived" : "Form re-activated");
      loadAll();
    } catch { toast.error("Failed to update form"); }
  };

  const duplicateTemplate = async (t) => {
    try {
      const { data } = await api.post(`/form_templates/${t.id}/duplicate`);
      toast.success("Form duplicated — opening the copy to edit");
      nav(`/app/forms/${data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };

  const deleteTemplate = (t) => setDeletingTemplate(t);
  const confirmDelete = async () => {
    const t = deletingTemplate;
    if (!t) return;
    try {
      await api.delete(`/form_templates/${t.id}`);
      toast.success("Form deleted");
      setDeletingTemplate(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // ---- Field worker view: pick & fill ----
  if (!isOffice) {
    const activeTemplates = templates.filter((t) => t.active && t.availability?.manual !== false && t.availability?.field_access !== false);
    return (
      <div data-testid="forms-page" className="p-6 lg:p-10 max-w-3xl mx-auto pb-24">
        <PageHeader
          tag="/ Custom forms"
          title="My Forms"
          subtitle="Your assigned work, saved drafts and completed forms."
        />
        <FormRequirements surface={["today", "job", "register"].includes(requestedSurface) ? requestedSurface : "register"} jobId={requestedJob} title="Assigned to me" />
        <h2 className="text-sm font-bold mt-6 mb-3">Available forms · start manually</h2>
        {loading ? (
          <div className="text-sm text-zinc-500 font-mono">Loading…</div>
        ) : activeTemplates.length === 0 ? (
          <EmptyState title="No forms available" description="Your office hasn't published any custom forms yet." />
        ) : (
          <div className="space-y-3" data-testid="field-form-list">
            {activeTemplates.map((t) => (
              <button
                key={t.id}
                onClick={() => setFillingTemplate(t)}
                className="w-full text-left hard-card p-5 flex items-center gap-4 hover:border-[#FF5F15] transition-colors"
                data-testid={`field-form-card-${t.id}`}
              >
                <div className="w-12 h-12 bg-[#09090B] text-white flex items-center justify-center shrink-0">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase tracking-tight">{t.name}</div>
                  {t.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.description}</div>}
                  <div className="text-[11px] text-zinc-400 font-mono mt-1.5">
                    {t.fields?.length || 0} questions
                    {t.category && t.category !== "general" && (
                      <span className="ml-2 text-[#FF5F15] font-bold uppercase tracking-wider">
                        {t.category === "safety" ? "Safety" : t.category === "job" ? "Job form" : "Fleet"}
                      </span>
                    )}
                  </div>
                </div>
                <Plus className="w-5 h-5 text-[#FF5F15]" />
              </button>
            ))}
          </div>
        )}

        {submissions.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-lg font-extrabold uppercase tracking-tight mb-3">My submissions</h2>
            <div className="space-y-2">
              {submissions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setViewingSubId(s.id)}
                  className="w-full text-left bg-white dark:bg-[#101620] border border-zinc-300 dark:border-white/10 p-3 flex items-center justify-between hover:border-[#FF5F15]"
                  data-testid={`field-sub-${s.id}`}
                >
                  <div>
                    <div className="font-bold text-sm">{s.template_name}{s.review_required && <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">{s.reviewed_at ? "Reviewed" : "Review required"}</span>}</div>
                    <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                      {formDate(s.completed_at||s.submitted_at||s.created_at)}
                      {s.job_id && (
                        <span className="ml-2 text-[#FF5F15] font-bold">
                          {s.job_number ? s.job_number : ""} {s.job_title || ""}
                        </span>
                      )}
                    </div>
                  </div>
                  <Eye className="w-4 h-4 text-zinc-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-3"><Button variant="outline" disabled={!subOffset} onClick={() => setSubOffset(v => Math.max(0, v - 50))}>Previous submissions</Button><Button variant="outline" disabled={submissions.length < 50} onClick={() => setSubOffset(v => v + 50)}>Next submissions</Button></div>
        <FormFillerDialog
          template={fillingTemplate}
          defaultJobId={fillJobId}
          onClose={() => { setFillingTemplate(null); setFillJobId(""); }}
          onSubmitted={() => { setFillingTemplate(null); setFillJobId(""); loadAll(); }}
        />
        <SubmissionViewerDialog
          submissionId={viewingSubId}
          onClose={() => setViewingSubId(null)}
        />
      </div>
    );
  }

  // ---- Office view: templates + submissions ----
  return (
    <div data-testid="forms-page" className="p-4 lg:p-6 w-full max-w-none min-h-screen bg-zinc-100 dark:bg-[#080e17] text-zinc-900 dark:text-zinc-100">
      <PageHeader
        tag="/ Dynamic forms"
        title="Custom Forms"
        subtitle="Manage field requirements, submissions and review."
        action={
          tab === "templates" ? (
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Button
                onClick={() => setLibraryOpen(true)}
                variant="outline"
                className="rounded-sm border-2 border-zinc-800 uppercase tracking-wider font-bold text-xs flex-1 md:flex-none"
                data-testid="browse-library-button"
              >
                <Globe className="w-4 h-4 mr-1.5" /> Library
              </Button>
              <Button
                onClick={openNewBuilder}
                className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold flex-1 md:flex-none"
                data-testid="new-form-button"
              >
                <Plus className="w-4 h-4 mr-2" /> New form
              </Button>
            </div>
          ) : null
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto border-b border-zinc-300 dark:border-white/15">
        <TabBtn active={tab === "overview"} onClick={()=>setTab("overview")}>Overview</TabBtn>
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testId="forms-tab-templates">
          Form library
        </TabBtn>
        <TabBtn active={tab === "assignments"} onClick={() => setTab("assignments")}>Assignments</TabBtn>
        <TabBtn active={tab === "submissions"} onClick={() => setTab("submissions")} testId="forms-tab-submissions">
          Submissions
        </TabBtn>
      </div>

      {tab === "templates" && <div className="flex flex-wrap gap-2 mb-4"><Input aria-label="Search form library" placeholder="Search forms" className="max-w-xs" value={libraryFilters.search} onChange={e=>{setLibraryFilters(f=>({...f,search:e.target.value}));setLibraryOffset(0);}}/>{!safetyContext&&<select aria-label="Library category" className="border rounded p-2 bg-white dark:bg-zinc-900" value={libraryFilters.category} onChange={e=>{setLibraryFilters(f=>({...f,category:e.target.value}));setLibraryOffset(0);}}><option value="">All categories</option>{['general','safety','job','fleet'].map(v=><option key={v}>{v}</option>)}</select>}<select aria-label="Library status" className="border rounded p-2 bg-white dark:bg-zinc-900" value={libraryFilters.active} onChange={e=>{setLibraryFilters(f=>({...f,active:e.target.value}));setLibraryOffset(0);}}><option value="">Active and archived</option><option value="true">Active</option><option value="false">Archived</option></select><Button variant="outline" disabled={!libraryOffset} onClick={()=>setLibraryOffset(v=>Math.max(0,v-30))}>Previous</Button><Button variant="outline" disabled={templates.length<30} onClick={()=>setLibraryOffset(v=>v+30)}>Next</Button></div>}
      {tab === "submissions" && <div className="mb-4 flex flex-wrap gap-2">
        <Input aria-label="Search submissions" className="max-w-xs" placeholder="Form, job or worker" value={filters.search||""} onChange={e=>{setFilters(f=>({...f,search:e.target.value}));setSubOffset(0);}}/>
        {!safetyContext&&<select aria-label="Submission category" className="border rounded p-2 bg-white dark:bg-zinc-900" value={filters.category||""} onChange={e=>{setFilters(f=>({...f,category:e.target.value}));setSubOffset(0);}}><option value="">All categories</option>{['general','safety','job','fleet'].map(v=><option key={v}>{v}</option>)}</select>}
        {Object.entries(assignmentOptions.date_presets||{}).map(([key,dates])=><Button key={key} variant="outline" onClick={()=>{setFilters(f=>({...f,...dates}));setSubOffset(0);}}>{key==='today'?'Today':key==='week'?'This week':'This month'}</Button>)}
        <label className="text-xs">Review<select className="block border rounded p-2 bg-white dark:bg-zinc-900" value={filters.review_state} onChange={e=>{setFilters(f=>({...f,review_state:e.target.value}));setSubOffset(0);}}><option value="">All submissions</option><option value="required">Review required</option><option value="reviewed">Reviewed</option><option value="not_required">Not required</option></select></label>
        <select aria-label="Filter submissions by form" value={filters.template_id} onChange={e => { setFilters(f => ({ ...f, template_id: e.target.value })); setSubOffset(0); }} className="border rounded p-2 bg-white dark:bg-zinc-900"><option value="">All forms</option>{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
        {[["job_id", "Job", assignmentOptions.jobs], ["worker_id", "Worker", assignmentOptions.workers]].map(([key, label, options]) => <label key={key} className="text-xs">{label}<select value={filters[key]} onChange={e => { setFilters(f => ({ ...f, [key]: e.target.value })); setSubOffset(0); }} className="block border rounded p-2 bg-white dark:bg-zinc-900"><option value="">All {label.toLowerCase()}s</option>{options.map(option => <option key={option.id} value={option.id}>{option.name || `${option.job_number || ""} ${option.title}`}</option>)}</select></label>)}
        {[["from_date", "From date"], ["to_date", "To date"]].map(([key, label]) => <label key={key} className="text-xs">{label}<Input type="date" value={filters[key]} onChange={e => { setFilters(f => ({ ...f, [key]: e.target.value })); setSubOffset(0); }} /></label>)}
        <Button variant="outline" disabled={!subOffset} onClick={() => setSubOffset(v => Math.max(0, v - 50))}>Previous</Button><Button variant="outline" disabled={submissions.length < 50} onClick={() => setSubOffset(v => v + 50)}>Next submissions</Button>
      </div>}
      {assigning && <FormAssignments templates={templates} templateId={assigning.id} onClose={() => setAssigning(null)} />}
      {tab === "overview" ? <FormsControlCentre safety={safetyContext} jobId={requestedJob} onSubmission={setViewingSubId} onNavigate={navigatePanel}/> : tab === "assignments" ? <><FormAssignments templates={templates} jobId={requestedJob} category={safetyContext ? "safety" : undefined} /><FormRequirements surface={safetyContext ? "safety" : "register"} title="Requirements and completion" jobId={requestedJob} officeControls /></> : loading ? (
        <div className="text-sm text-zinc-500 font-mono">Loading…</div>
      ) : tab === "templates" ? (
        templates.length === 0 ? (
          <EmptyState title="No forms yet"
            description="Create your first form. Add the questions you need — text, photos, signatures, GPS, anything." />
        ) : (
          <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))]">
            {templates.map((t) => (
              <div key={t.id} className={`border border-zinc-200 dark:border-white/10 rounded-lg bg-white dark:bg-[#101620] p-4 ${t.active ? "" : "border-dashed"}`} data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight text-base truncate">{t.name}</div>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{t.active_assignment_count||0} active assignments · Updated {t.updated_at ? formDate(t.updated_at) : '—'}</p>
                    <div className="flex gap-2 mt-2"><button className="text-xs underline" onClick={()=>setAssigning(t)}>Assignments</button><button className="text-xs underline" onClick={()=>navigatePanel('submissions',{template_id:t.id})}>Submissions</button></div>
                    {t.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.description}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {!t.active && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-200 text-zinc-700 border border-zinc-400 px-1.5 py-0.5 rounded-sm">
                        Archived
                      </span>
                    )}
                    {t.category && t.category !== "general" && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest text-[#FF5F15] border border-[#FF5F15] px-1 py-0.5" data-testid={`template-category-badge-${t.id}`}>
                        {t.category === "safety" ? <><ShieldCheck className="w-2.5 h-2.5" /> Safety</>
                          : t.category === "job" ? <><Briefcase className="w-2.5 h-2.5" /> Job form</>
                          : <><Truck className="w-2.5 h-2.5" /> Fleet</>}
                      </span>
                    )}
                    {t.shared && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-700 border border-emerald-400 bg-emerald-50 px-1 py-0.5" data-testid={`template-shared-badge-${t.id}`}>
                        <Globe className="w-2.5 h-2.5" /> In library
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 text-[11px] text-zinc-500 font-mono flex flex-wrap items-center gap-2">
                  <span>{t.fields?.length || 0} questions</span>
                  <span>·</span>
                  <span>{t.submission_count || 0} submissions</span>
                  {t.category === "job" && (<><span>·</span><span className="text-[#FF5F15] font-bold" data-testid={`template-jobscope-${t.id}`}>{(t.job_ids || []).length ? `${t.job_ids.length} job${t.job_ids.length === 1 ? "" : "s"}` : "all jobs"}</span></>)}
                  {t.imported_from && (<><span>·</span><span className="text-zinc-400">from {t.imported_from}</span></>)}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEditBuilder(t)}
                          className="rounded-sm text-xs font-bold uppercase tracking-wider border-zinc-400"
                          data-testid={`template-edit-${t.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setFillingTemplate(t)}
                          className="rounded-sm text-xs font-bold uppercase tracking-wider border-zinc-400"
                          data-testid={`template-preview-${t.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                  </Button>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={() => archiveTemplate(t)}
                          className="rounded-sm text-xs font-bold uppercase tracking-wider"
                          data-testid={`template-toggle-${t.id}`}>
                    {t.active ? "Archive" : "Re-activate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteTemplate(t)}
                          className="rounded-sm text-xs font-bold uppercase tracking-wider text-red-700 border-red-300 hover:bg-red-50"
                          data-testid={`template-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
                <Button size="sm" variant="outline" onClick={() => setAssigning(t)} className="mt-2 w-full">Assignments / Assign form</Button>
                <Button size="sm" variant="outline" onClick={() => duplicateTemplate(t)}
                        className="mt-2 w-full rounded-sm text-xs font-bold uppercase tracking-wider border-zinc-400"
                        data-testid={`template-duplicate-${t.id}`}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
                </Button>
                <Button size="sm" variant="outline" onClick={() => shareTemplate(t)}
                        className={`mt-2 w-full rounded-sm text-xs font-bold uppercase tracking-wider ${t.shared ? "border-emerald-400 text-emerald-700 hover:bg-emerald-50" : "border-zinc-400"}`}
                        data-testid={`template-share-${t.id}`}>
                  <Globe className="w-3.5 h-3.5 mr-1" /> {t.shared ? "Unshare from library" : "Share to library"}
                </Button>
              </div>
            ))}
          </div>
        )
      ) : (
        submissions.length === 0 ? (
          <EmptyState title="No submissions yet" description="Once your crew fills out a form, you'll see the results here." />
        ) : (
          <div className="border rounded-lg bg-white dark:bg-[#101620] border-zinc-200 dark:border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 dark:bg-zinc-900 border-b border-zinc-300 dark:border-white/10 text-left">
                <tr>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">Form</th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">Job</th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">Submitted by</th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">When</th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-200 dark:border-white/10 hover:bg-zinc-50 dark:hover:bg-white/5" data-testid={`submission-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium">{s.template_name}{s.review_required && <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">{s.reviewed_at ? "Reviewed" : "Review required"}</span>}</td>
                    <td className="px-4 py-3 text-xs">
                      <FormContext item={s}/>
                    </td>
                    <td className="px-4 py-3">{s.user_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600 dark:text-zinc-400">{formDate(s.completed_at||s.submitted_at||s.created_at)}</td>
                    <td className="px-4 py-3 text-xs">Completed · {s.evidence_count||0} evidence file(s)</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setViewingSubId(s.id)}
                              className="rounded-sm text-xs font-bold uppercase tracking-wider"
                              data-testid={`submission-view-${s.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <TemplateLibraryDialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onImported={() => { setLibraryOpen(false); loadAll(); }}
      />
      <FormFillerDialog
        template={fillingTemplate}
        defaultJobId={fillJobId}
        onClose={() => { setFillingTemplate(null); setFillJobId(""); }}
        onSubmitted={() => { setFillingTemplate(null); setFillJobId(""); loadAll(); }}
      />
      <SubmissionViewerDialog
        submissionId={viewingSubId}
        onClose={() => setViewingSubId(null)}
      />
      <Dialog open={!!deletingTemplate} onOpenChange={(o) => { if (!o) setDeletingTemplate(null); }}>
        <DialogContent className="rounded-xl max-w-md" data-testid="delete-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Delete "{deletingTemplate?.name}"?</DialogTitle>
            <DialogDescription>
              Only unused forms can be deleted. Forms with assignments or submissions must be archived to preserve historical records.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingTemplate(null)} className="rounded-sm uppercase tracking-wider font-bold" data-testid="delete-form-cancel">Cancel</Button>
            <Button onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700 text-white uppercase tracking-wider font-bold" data-testid="delete-form-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 -mb-px text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
        active ? "border-[#FF5F15] text-[#09090B] dark:text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-800"
      }`}
    >
      {children}
    </button>
  );
}

// ============================================================
//  FILLER DIALOG (any role)
// ============================================================
export function FormFillerDialog({ template, onClose, onSubmitted, defaultJobId = "", lockJob = false }) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");

  useEffect(() => {
    if (template) {
      const initial = {};
      for (const f of template.fields || []) {
        if (f.type === "checkbox" || f.type === "section" && f.repeatable) initial[f.id] = [];
        else if (f.type === "yes_no") initial[f.id] = null;
        else initial[f.id] = "";
      }
      setValues(initial);
      setJobId(defaultJobId || "");
      api.get("/jobs")
        .then((r) => setJobs((r.data || []).filter((j) => j.status !== "completed")))
        .catch(() => setJobs([]));
    }
  }, [template, defaultJobId]);

  if (!template) return null;

  const jobRequired = template.category === "job";
  const allowedJobs = jobRequired && (template.job_ids || []).length
    ? jobs.filter((j) => template.job_ids.includes(j.id))
    : jobs;
  const setVal = (fid, v) => setValues((s) => ({ ...s, [fid]: v }));

  const submit = async () => {
    if (jobRequired && !jobId) {
      toast.error("This form must be assigned to a job");
      return;
    }
    const errors = formErrors(template.fields, values);
    if (errors.length) { toast.error(`${errors[0].label}: ${errors[0].message}`); focusFormError(errors); return; }
    setSubmitting(true);
    try {
      await api.post("/form_submissions", { template_id: template.id, values, job_id: jobId || null });
      toast.success("Form submitted");
      onSubmitted();
    } catch (e) {
      toast.error(formErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!template} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-xl w-[min(760px,calc(100vw-32px))] max-w-none max-h-[calc(100vh-48px)] overflow-y-auto" data-testid="form-filler-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">{template.name}</DialogTitle>
          {template.description && (
            <DialogDescription>{template.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-5">
          <div className="bg-zinc-50 border border-zinc-300 p-3">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-600 block mb-1.5">
              {jobRequired ? <>Job <span className="text-[#FF5F15]">*</span> <span className="font-normal normal-case text-zinc-400">(this form is filled against a job)</span></>
                : <>Job <span className="font-normal normal-case text-zinc-400">(optional — shows in the job's completion pack)</span></>}
            </label>
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              disabled={lockJob && !!defaultJobId}
              className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-[#FF5F15] disabled:bg-zinc-100 disabled:text-zinc-500"
              data-testid="filler-job-select"
            >
              <option value="">{jobRequired ? "Select a job…" : "No job — general submission"}</option>
              {allowedJobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {(j.job_number ? j.job_number + " — " : "") + j.title}
                </option>
              ))}
            </select>
          </div>
          <FormFields fields={template.fields} values={values} onChange={setVal} />
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold" data-testid="filler-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                  data-testid="filler-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Submit form"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FillerField({ field, value, onChange, onlineOnly = false }) {
  const [busy, setBusy] = useState(false);
  const [showSig, setShowSig] = useState(false);
  const [sigCapture, setSigCapture] = useState(null); // dataUrl kept for retry-without-redraw
  const [sigError, setSigError] = useState("");

  if (field.type === "section") {
    return (
      <div className="pt-3 border-t-2 border-[#FF5F15]/60" data-testid={`filler-section-${field.id}`}>
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-[#FF5F15]">Section</div>
        <div className="font-display font-black uppercase tracking-tight text-lg mt-0.5">{field.label}</div>
        {field.help_text && <div className="text-xs text-zinc-500 mt-0.5">{field.help_text}</div>}
      </div>
    );
  }

  const labelEl = (
    <div className="mb-1.5">
      <Label className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
        {field.label} {field.required && <span className="text-red-600">*</span>}
      </Label>
      {field.help_text && <p className="text-xs text-zinc-500 mt-0.5">{field.help_text}</p>}
    </div>
  );

  switch (field.type) {
    case "short_text":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
                 placeholder={field.placeholder || ""}
                 className="rounded-sm border-zinc-400" />
        </div>
      );
    case "long_text":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)}
                    placeholder={field.placeholder || ""}
                    className="rounded-sm border-zinc-400" />
        </div>
      );
    case "currency":
    case "number":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                 placeholder={field.placeholder || ""}
                 className="rounded-sm border-zinc-400" />
        </div>
      );
    case "quantity":
      return <div>{labelEl}<div className="flex gap-2"><Input inputMode="decimal" value={value?.quantity ?? ""} onChange={e=>onChange({...value,quantity:e.target.value})} aria-label="Quantity" /><select aria-label="Unit" className="bg-transparent border rounded p-2" value={value?.unit || ""} onChange={e=>onChange({...value,unit:e.target.value})}><option value="">Unit…</option>{(field.units||[]).map(u=><option key={u}>{u}</option>)}</select></div></div>;
    case "datetime":
      return <div>{labelEl}<Input type="datetime-local" value={value||""} onChange={e=>onChange(e.target.value)} /></div>;
    case "date":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
                 className="rounded-sm border-zinc-400 font-mono" />
        </div>
      );
    case "time":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <Input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)}
                 className="rounded-sm border-zinc-400 font-mono" />
        </div>
      );
    case "yes_no":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <div className="flex gap-2">
            <Button type="button" size="sm" style={{minHeight:44}} variant={value === true ? "default" : "outline"}
                    className={`rounded-sm uppercase font-bold ${value === true ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
                    onClick={() => onChange(true)} data-testid={`filler-yes-${field.id}`}>Yes</Button>
            <Button type="button" size="sm" style={{minHeight:44}} variant={value === false ? "default" : "outline"}
                    className={`rounded-sm uppercase font-bold ${value === false ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}
                    onClick={() => onChange(false)} data-testid={`filler-no-${field.id}`}>No</Button>
          </div>
        </div>
      );
    case "radio":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <div className="space-y-1.5">
            {field.options.map((o, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="radio" name={field.id} value={optionId(o)} checked={value === optionId(o)}
                       onChange={(e) => onChange(e.target.value)} className="accent-[#FF5F15]" />
                <span>{optionLabel(o)}</span>
              </label>
            ))}
          </div>
        </div>
      );
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <div className="space-y-1.5">
            {field.options.map((o, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer text-sm">
                <Checkbox checked={arr.includes(optionId(o))} onCheckedChange={() => toggle(optionId(o))} />
                <span>{optionLabel(o)}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    case "dropdown":
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger className="rounded-sm border-zinc-400"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {field.options.map((o, i) => <SelectItem key={i} value={optionId(o)}>{optionLabel(o)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    case "photo": {
      const paths = Array.isArray(value) ? value : (value ? [value] : []);
      const onPick = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setBusy(true);
        try {
          const up = [];
          for (const f of files.slice(0, Math.max(0, (field.max_photos || 10) - paths.length))) up.push({path: await uploadFile(f, "form_photo", true), caption: ""});
          onChange([...paths, ...up]);
          toast.success(`Uploaded ${up.length} photo(s)`);
        } catch { toast.error("Upload failed"); }
        finally { setBusy(false); e.target.value = ""; }
      };
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-400 p-4 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15]">
            <Camera className="w-4 h-4" />
            <span className="text-sm font-bold uppercase tracking-wider">
              {busy ? "Uploading…" : "Add photo(s)"}
            </span>
            <input type="file" accept="image/*" multiple disabled={busy || paths.length >= (field.max_photos||10)} className="hidden" onChange={onPick} data-testid={`filler-photo-input-${field.id}`} />
          </label>
          <label className="inline-flex min-h-11 items-center gap-2 text-sm cursor-pointer"><Camera className="w-4 h-4"/>Take photo<input type="file" accept="image/*" capture="environment" disabled={busy || paths.length >= (field.max_photos||10)} className="hidden" onChange={onPick}/></label>
          <p className="text-xs text-zinc-500">{paths.length} / {field.max_photos||10} photos · Connection required</p>
          {paths.length > 0 && (
            <div className="mt-3 space-y-2"><PhotoGrid paths={paths.map(p=>typeof p === "string"?p:p.path)} />{paths.map((p,i)=><div key={i} className="flex gap-2"><Input aria-label={`Photo ${i+1} caption`} placeholder="Photo caption (optional)" value={p.caption||""} onChange={e=>onChange(paths.map((item,j)=>j===i?{path:typeof item==="string"?item:item.path,caption:e.target.value}:item))}/><Button type="button" variant="outline" onClick={()=>onChange(paths.filter((_,j)=>j!==i))}>Remove</Button></div>)}</div>
          )}
        </div>
      );
    }
    case "file": {
      const paths = Array.isArray(value) ? value : (value ? [value] : []);
      const onPick = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setBusy(true);
        try {
          const up = [];
          for (const f of files) up.push({ path: await uploadFile(f, "form_file", onlineOnly), name: f.name });
          onChange([...paths, ...up]);
          toast.success(`Uploaded ${up.length} file(s)`);
        } catch { toast.error("Upload failed"); }
        finally { setBusy(false); e.target.value = ""; }
      };
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-400 p-4 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15]">
            <Paperclip className="w-4 h-4" />
            <span className="text-sm font-bold uppercase tracking-wider">
              {busy ? "Uploading…" : "Attach file(s)"}
            </span>
            <input type="file" multiple className="hidden" onChange={onPick} data-testid={`filler-file-input-${field.id}`} />
          </label>
          {paths.length > 0 && (
            <ul className="mt-2 text-xs font-mono space-y-1">
              {paths.map((p, i) => (
                <li key={i} className="flex items-center gap-2"><Paperclip className="w-3 h-3" /> {p.name || p.path?.split("/").pop()}</li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    case "signature": {
      // Upload a captured signature. On failure we keep the dataUrl so the
      // worker can retry without redrawing, and we NEVER mark the field saved.
      const doUpload = async (dataUrl) => {
        if (!dataUrl) return;
        setBusy(true); setSigError("");
        try {
          const path = await uploadDataUrl(dataUrl, "form_signature", onlineOnly);
          if (!path || typeof path !== "string") throw new Error("no-path");
          onChange(path);
          setSigCapture(null);
          setShowSig(false);
          if (path.startsWith("offline://")) {
            toast.success("Signature saved on your phone — it'll upload when you're back online");
          } else {
            toast.success("Signature saved");
          }
        } catch {
          setSigCapture(dataUrl);
          setSigError("Your signature was captured but couldn't be saved. Check your connection and retry — you won't need to draw it again.");
        } finally {
          setBusy(false);
        }
      };
      const isOfflinePending = typeof value === "string" && value.startsWith("offline://");
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          {sigError ? (
            <div className="border border-red-300 bg-red-50 p-3 space-y-2">
              <p className="text-sm text-red-700 font-medium" role="alert" data-testid={`filler-sign-${field.id}-error`}>{sigError}</p>
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => doUpload(sigCapture)}
                        className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-xs font-bold uppercase tracking-wider"
                        data-testid={`filler-sign-${field.id}-retry`}>
                  {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Retrying…</> : "Retry save"}
                </Button>
                <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => { setSigError(""); setSigCapture(null); setShowSig(true); }}
                        className="rounded-sm text-xs font-bold uppercase tracking-wider"
                        data-testid={`filler-sign-${field.id}-redraw`}>
                  Draw again
                </Button>
              </div>
            </div>
          ) : value && !showSig ? (
            <div className="border border-zinc-300 bg-white p-2" data-testid={`filler-sign-${field.id}-saved`}>
              {isOfflinePending ? (
                <p className="text-sm text-amber-700 font-medium" data-testid={`filler-sign-${field.id}-pending`}>
                  ✓ Signature captured — saved on your phone. It will upload automatically when you're back online.
                </p>
              ) : (
                <AuthImage path={value} alt="Signature" className="h-16 object-contain" />
              )}
              <Button size="sm" variant="outline" onClick={() => setShowSig(true)}
                      className="rounded-sm text-xs font-bold uppercase tracking-wider mt-2"
                      data-testid={`filler-sign-${field.id}-resign`}>
                <PenLine className="w-3.5 h-3.5 mr-1" /> Re-sign
              </Button>
            </div>
          ) : showSig ? (
            <SignaturePad testIdPrefix={`sig-${field.id}`} busy={busy}
                          onCancel={() => setShowSig(false)} onSave={doUpload} />
          ) : (
            <Button size="sm" variant="outline" onClick={() => setShowSig(true)}
                    className="rounded-sm text-xs font-bold uppercase tracking-wider"
                    data-testid={`filler-sign-${field.id}`}>
              <PenLine className="w-3.5 h-3.5 mr-1" /> Sign here
            </Button>
          )}
        </div>
      );
    }
    case "location": {
      const captureGps = () => {
        if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
        setBusy(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
            setBusy(false);
            toast.success("Location captured");
          },
          (err) => { setBusy(false); toast.error(err.message || "Failed to get location"); },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      };
      return (
        <div data-testid={`filler-${field.id}`}>{labelEl}
          {value?.lat ? (
            <div className="border border-zinc-300 bg-zinc-50 dark:bg-zinc-900 p-3 font-mono text-xs flex items-center justify-between">
              <span><MapPin className="w-3.5 h-3.5 inline mr-1" /> {value.lat.toFixed(6)}, {value.lng.toFixed(6)}</span>
              <Button size="sm" variant="outline" onClick={captureGps} disabled={busy}
                      className="rounded-sm text-[10px] font-bold uppercase tracking-wider">Refresh</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={captureGps} disabled={busy}
                    className="rounded-sm text-xs font-bold uppercase tracking-wider"
                    data-testid={`filler-gps-${field.id}`}>
              <MapPin className="w-3.5 h-3.5 mr-1" /> {busy ? "Capturing…" : "Capture GPS pin"}
            </Button>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}

// ============================================================
//  SUBMISSION VIEWER
// ============================================================
export function SubmissionViewerDialog({ submissionId, onClose }) {
  const {user} = useAuth();
  const office = ['admin','office','super_admin'].includes(user?.role);
  const [reviewNote,setReviewNote] = useState(''),[reviewing,setReviewing] = useState(false);
  const markReviewed = async () => {setReviewing(true);try{await api.post(`/form_submissions/${submissionId}/review`,{note:reviewNote});const r=await api.get(`/form_submissions/${submissionId}`);setData(r.data);window.dispatchEvent(new Event('forms-changed'));toast.success('Marked reviewed');}catch(e){toast.error(formErrorMessage(e));}finally{setReviewing(false);}};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/form_submissions/${submissionId}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(data?.template_name || "form").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setReviewNote("");
    if (!submissionId) { setData(null); return; }
    setLoading(true);
    api.get(`/form_submissions/${submissionId}`)
       .then((r) => setData(r.data))
       .catch(() => { toast.error("Failed to load submission"); onClose(); })
       .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const photoPaths = useMemo(() => {
    if (!data?.template) return [];
    const out = formRows(data.template.fields || [], data.values || {}).filter(r=>r.visible).flatMap(r=>[...(r.field.type === "photo" ? (Array.isArray(r.value)?r.value:r.value?[r.value]:[]) : []), ...(triggeredRules(r.field,r.value).some(rule=>rule.photo)?r.evidence?.photos||[]:[])]).map(p=>typeof p === 'string'?p:p.path);
    return out;
  }, [data]);

  return (
    <Dialog open={!!submissionId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-xl w-[min(760px,calc(100vw-32px))] max-w-none max-h-[calc(100vh-48px)] overflow-y-auto bg-white dark:bg-[#101620] text-zinc-900 dark:text-zinc-100" data-testid="submission-viewer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            {data?.template_name || "Submission"}
          </DialogTitle>
          {data && (
            <DialogDescription>
              Submitted by <b>{data.user_name}</b> on {new Date(data.created_at).toLocaleString("en-NZ")}
              {data.job_id && (
                <span className="block mt-1 text-[#FF5F15] font-semibold" data-testid="submission-job-label">
                  Job: {(data.job_number ? data.job_number + " — " : "") + (data.job_title || "")}
                </span>
              )}
            </DialogDescription>
          )}
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-500 font-mono">Loading…</div>
        ) : data ? (
          <div className="space-y-4">
            {data.review_required && <div role="status" className="border border-amber-500 rounded p-3"><b>{data.reviewed_at ? "Reviewed" : "Review required"} · {data.review_items?.length||0} flagged responses</b><ul>{(data.review_items||[]).map(r=><li key={r.path}>{r.row?`Entry ${r.row} · `:""}{r.label}</li>)}</ul></div>}
            {formRows(data.template?.fields || [], data.values || {}).filter(r=>r.visible).map(r=><div key={r.path} className={`space-y-2 ${data.review_items?.some(item=>item.path===r.path) ? "border-l-4 border-amber-500 pl-3" : ""}`}>
              {r.index!=null && <p className="text-xs font-bold">Entry {r.index+1}</p>}
              <SubmissionAnswer field={r.field} value={r.value} onOpenPhoto={p=>setLightbox({paths:photoPaths,index:Math.max(0,photoPaths.indexOf(p))})}/>
              {triggeredRules(r.field,r.value).some(rule=>rule.comment) && r.evidence.comment && <p className="text-sm whitespace-pre-wrap">Comment: {r.evidence.comment}</p>}
              {triggeredRules(r.field,r.value).some(rule=>rule.photo) && !!r.evidence.photos?.length && <SubmissionAnswer field={{type:"photo",label:"Supporting photos"}} value={r.evidence.photos}/>}
            </div>)}
            {!!data.evidence_files?.length && <details className="text-xs"><summary>Evidence upload details</summary>{data.evidence_files.map(f=><p key={f.path}>{f.uploader_id} · {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString():"Time unavailable"}</p>)}</details>}
            {data.reviewed_at && <div className="border rounded p-3 text-sm"><b>Reviewed</b><p>{data.reviewed_by_name||'Office user'} · {formDate(data.reviewed_at)}</p>{office&&data.review_note&&<p className="whitespace-pre-wrap mt-2">{data.review_note}</p>}</div>}
            {office && data.review_required && !data.reviewed_at && data.status !== 'draft' && <div className="border rounded p-3 space-y-2"><label className="text-sm font-bold">Internal review note (optional)<Textarea maxLength={4000} value={reviewNote} onChange={e=>setReviewNote(e.target.value)}/></label><p className="text-xs text-zinc-500 dark:text-zinc-400">Records that you reviewed the flagged submission. Original responses and evidence remain unchanged.</p><Button disabled={reviewing} onClick={markReviewed}>{reviewing?'Saving…':'Mark reviewed'}</Button></div>}
            {(data.template?.fields || []).length === 0 && (
              <div className="text-sm text-zinc-500 italic">This form has no questions.</div>
            )}
          </div>
        ) : null}
        <DialogFooter>
          {data && (
            <Button onClick={exportPdf} disabled={exporting}
                    className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase tracking-wider font-bold text-xs"
                    data-testid="submission-export-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1.5" />
              {exporting ? "Exporting…" : "Export PDF"}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold" data-testid="submission-viewer-close">
            Close
          </Button>
        </DialogFooter>
        {lightbox && (
          <PhotoLightbox paths={lightbox.paths} startIndex={lightbox.index} onClose={() => setLightbox(null)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SubmissionAnswer({ field, value, onOpenPhoto }) {
  if(field.type === "section") return <div className="border-t pt-3"><h3 className="font-bold text-lg">{field.label}</h3><p className="text-sm text-zinc-500">{field.help_text}</p></div>;
  const label = (
    <div className="label-tag mb-1">{field.label}</div>
  );
  const empty = <div className="text-sm text-zinc-400 italic">—</div>;

  let body = null;
  if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
    body = empty;
  } else {
    switch (field.type) {
      case "yes_no":
        body = (
          <span className={`inline-block px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm ${value ? "bg-green-100 text-green-800 border border-green-300" : "bg-red-100 text-red-800 border border-red-300"}`}>
            {value ? "Yes" : "No"}
          </span>
        );
        break;
      case "quantity": body = <p>{value.quantity} {value.unit}</p>; break;
      case "radio":
      case "dropdown": body = <p>{optionLabel((field.options||[]).find(o=>optionId(o)===value))||String(value)}</p>; break;
      case "checkbox":
        body = (
          <div className="flex flex-wrap gap-1.5">
            {(Array.isArray(value) ? value : []).map((v, i) => (
              <span key={i} className="text-[11px] font-mono bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-white/10 px-2 py-0.5 rounded-sm">{optionLabel((field.options||[]).find(o=>optionId(o)===v))||v}</span>
            ))}
          </div>
        );
        break;
      case "photo": {
        const items = Array.isArray(value) ? value : [value];
        const paths = items.map(p=>typeof p === "string"?p:p.path);
        body = <div><PhotoGrid paths={paths} onClick={(p) => onOpenPhoto && onOpenPhoto(p)} />{items.map((p,i)=>p.caption?<p key={i} className="text-xs">Photo {i+1}: {p.caption}</p>:null)}</div>;
        break;
      }
      case "file": {
        const items = Array.isArray(value) ? value : [value];
        body = (
          <ul className="text-xs font-mono space-y-1">
            {items.map((it, i) => {
              const path = typeof it === "string" ? it : it?.path;
              const name = typeof it === "string" ? it.split("/").pop() : (it?.name || path?.split("/").pop());
              return (
                <li key={i}>
                  <a href={`/api/files/view?path=${encodeURIComponent(path)}&auth=${localStorage.getItem("fl_token")}`}
                     target="_blank" rel="noreferrer" className="underline text-[#FF5F15] inline-flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> {name}
                  </a>
                </li>
              );
            })}
          </ul>
        );
        break;
      }
      case "signature":
        body = (
          <div className="border border-zinc-300 bg-white p-2 inline-block">
            <AuthImage path={value} alt="Signature" className="h-14 object-contain" />
          </div>
        );
        break;
      case "location":
        if (value?.lat) {
          body = (
            <div className="font-mono text-sm">
              <a href={`https://maps.google.com/?q=${value.lat},${value.lng}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 underline text-[#FF5F15]">
                <MapPin className="w-3.5 h-3.5" /> {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
              </a>
            </div>
          );
        } else body = empty;
        break;
      case "long_text":
        body = <p className="text-sm whitespace-pre-wrap text-zinc-800 dark:text-zinc-100">{String(value)}</p>;
        break;
      default:
        body = <div className="text-sm text-zinc-800 dark:text-zinc-100">{String(value)}</div>;
    }
  }

  return (
    <div className="border-b border-zinc-200 pb-3 last:border-b-0">
      {label}
      {body}
    </div>
  );
}

// All fill contexts share these controls and applicability rules.
export function FormFields({fields,values,onChange,onlineOnly=true,preview=false}) {
  const rows=formRows(fields,values).filter(r=>r.visible),errors=formErrors(fields,values);
  const questions=rows.filter(r=>r.field.type!=='section');
  const update=(r,value,evidence=false)=>{
    if(r.section){const entries=[...(values[r.section]||[])],entry={...entries[r.index]}; if(evidence)entry.__evidence={...entry.__evidence,[r.field.id]:value};else entry[r.field.id]=value;entries[r.index]=entry;onChange(r.section,entries);}
    else if(evidence)onChange('__evidence',{...values.__evidence,[r.field.id]:value});else onChange(r.field.id,value);
  };
  return <div className="space-y-5"><p className="text-xs text-zinc-500" aria-live="polite">{questions.filter(r=>!emptyAnswer(r.value)).length} / {questions.length} visible questions answered · {errors.length} items need attention</p>
    {rows.map(r=>{const f=r.field,rules=triggeredRules(f,r.value),messages=errors.filter(e=>e.path===r.path);const following=rows.slice(rows.indexOf(r)+1),nextSection=following.findIndex(x=>x.field.type==='section'),sectionRows=following.slice(0,nextSection<0?undefined:nextSection);return <div id={`answer-${r.path}`} key={r.path} tabIndex={-1} className="space-y-2 scroll-mt-20">
      {f.type==='section' ? <div className="border-t pt-4"><h2 className="font-bold text-lg">{f.label}</h2><p className="text-xs text-zinc-500">{sectionRows.filter(x=>!emptyAnswer(x.value)).length} / {sectionRows.length} answered</p><p className="text-sm text-zinc-500">{f.help_text}</p>{f.repeatable && <div className="flex gap-2 flex-wrap mt-2"><Button type="button" variant="outline" disabled={(r.value?.length||0)>=(f.max_entries||20)} onClick={()=>onChange(f.id,[...(r.value||[]),{}])}>Add entry</Button>{(r.value||[]).map((_,i)=><Button type="button" key={i} variant="outline" onClick={()=>{if(window.confirm(`Remove entry ${i+1} and its answers?`))onChange(f.id,r.value.filter((_,j)=>i!==j));}}>Remove entry {i+1}</Button>)}</div>}</div> : <>
        {r.index!=null && <p className="text-xs font-bold text-zinc-500">Entry {r.index+1}</p>}
        <fieldset disabled={preview && ['photo','file','signature','location'].includes(f.type)}><FillerField field={{...f,id:r.path}} value={r.value} onChange={v=>update(r,v)} onlineOnly={onlineOnly}/></fieldset>
        {rules.some(rule=>rule.review) && <p className="text-sm text-amber-700 dark:text-amber-300">This answer requires office review.</p>}
        {rules.some(rule=>rule.comment) && <label className="block text-sm">Comment required<Textarea value={r.evidence.comment||''} onChange={e=>update(r,{...r.evidence,comment:e.target.value},true)}/></label>}
        {rules.some(rule=>rule.photo) && <fieldset disabled={preview}><FillerField field={{id:`${r.path}-evidence`,label:'Supporting photo required',type:'photo',required:true,max_photos:10}} value={r.evidence.photos||[]} onChange={v=>update(r,{...r.evidence,photos:v},true)} onlineOnly/></fieldset>}
      </>}
      {messages.map((e,i)=><p key={i} className="text-xs text-red-700 dark:text-red-300">{e.message}</p>)}
    </div>;})}
  </div>;
}
